import React, { useState, useEffect } from 'react';
import {
  Box, Grid, TextField, MenuItem, Button, Alert, CircularProgress, Typography, Chip, Stack,
} from '@mui/material';
import { CloudUpload } from '@mui/icons-material';
import api from '../../services/api';

// Bulk DID import — same endpoint as the Numbers page (POST /numbers/import).
// Imported DIDs are inactive (503 → old switch) until gone-live on the Numbers page.
export const DidImportTab: React.FC = () => {
  const [numbers, setNumbers] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [numberType, setNumberType] = useState('geographic');
  const [countryCode, setCountryCode] = useState('');
  const [carriers, setCarriers] = useState<any[]>([]);
  const [tenants, setTenants] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    api.get('/carriers?limit=200').then(({ data }) => setCarriers(data.data || [])).catch(() => {});
    api.get('/tenants?limit=200').then(({ data }) => setTenants((data.data || []).filter((t: any) => t.tier !== 'provider'))).catch(() => {});
  }, []);

  const count = numbers.split(/[\s,;]+/).filter(n => /\d/.test(n)).length;

  const run = async () => {
    setBusy(true); setError(''); setResult(null);
    try {
      const { data } = await api.post('/numbers/import', {
        numbers, carrier_id: carrierId || undefined, tenant_id: tenantId || undefined,
        number_type: numberType, country_code: countryCode || undefined,
      });
      setResult(data);
    } catch (e: any) { setError(e.response?.data?.message || 'Import failed'); }
    finally { setBusy(false); }
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} gutterBottom>Import DIDs</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Load a BT-issued DID range. Numbers land <b>inactive (503 → old switch)</b> and become active only when you
        Go Live on the DID Numbers page — this drives the per-number migration.
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {result && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setResult(null)}>
          Imported <b>{result.created}</b> DID(s){result.skipped?.length ? <> · skipped {result.skipped.length} already present</> : null}.
          Go to <b>DID Numbers</b> to assign/Go-Live.
        </Alert>
      )}
      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <TextField fullWidth multiline minRows={10} label="DID numbers"
            value={numbers} onChange={e => setNumbers(e.target.value)}
            placeholder={'One per line (or comma/space separated)\n+442071234500\n+442071234501\n+442071234502'} />
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <Chip label={`${count} number(s) detected`} size="small" color={count ? 'primary' : 'default'} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <TextField select fullWidth label="Carrier (issuer)" value={carrierId} onChange={e => setCarrierId(e.target.value)}>
              <MenuItem value=""><em>— None —</em></MenuItem>
              {carriers.filter(c => c.status === 'active').map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Assign to customer (optional)" value={tenantId} onChange={e => setTenantId(e.target.value)}>
              <MenuItem value=""><em>Leave in inventory</em></MenuItem>
              {tenants.map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
            </TextField>
            <TextField select fullWidth label="Number Type" value={numberType} onChange={e => setNumberType(e.target.value)}>
              {['geographic', 'mobile', 'tollfree', 'premium', 'shortcode'].map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
            <TextField fullWidth label="Country Code" value={countryCode} onChange={e => setCountryCode(e.target.value)} placeholder="e.g. 44" />
            <Button variant="contained" startIcon={busy ? <CircularProgress size={18} /> : <CloudUpload />}
              onClick={run} disabled={busy || !count}>
              Import {count || ''} DID(s)
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};
